import { useCallback, useMemo, useState } from 'react';
import {
  Animated,
  Keyboard,
  KeyboardAvoidingView,
  Modal,
  Pressable,
  StyleSheet,
  TouchableWithoutFeedback,
  View,
  type ViewStyle,
} from 'react-native';
import { Button, IconButton, PaperProvider, Text, TouchableRipple } from 'react-native-paper';
import { TimePicker } from 'react-native-paper-dates';

import { useTranslation } from '@/i18n';
import { rippleOnSurface, unisonColors, useAppTheme } from '@/theme';

interface MeetingDateTimePickerDialogProps {
  readonly visible: boolean;
  readonly initialDateTime: string;
  readonly locale: string;
  readonly onDismiss: () => void;
  readonly onConfirm: (dateTime: string) => void;
}

const CARD_SHADOW: ViewStyle = {
  elevation: 5,
  shadowColor: '#254157',
  shadowOffset: { width: 0, height: 5 },
  shadowOpacity: 0.2,
  shadowRadius: 13,
};

const styles = StyleSheet.create({
  overlay: {
    alignItems: 'center',
    backgroundColor: 'rgba(0,0,0,0.5)',
    flex: 1,
    justifyContent: 'center',
    paddingHorizontal: 16,
  },
  dismissArea: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
  },
  card: {
    backgroundColor: unisonColors.white,
    borderRadius: 16,
    maxWidth: 360,
    overflow: 'hidden',
    width: '100%',
  },
  selectionRow: {
    alignItems: 'center',
    flexDirection: 'row',
    height: 64,
    justifyContent: 'space-between',
    paddingHorizontal: 12,
  },
  selectionGroup: {
    alignItems: 'center',
    flexDirection: 'row',
  },
  iconButton: {
    alignItems: 'center',
    borderRadius: 24,
    height: 48,
    justifyContent: 'center',
    overflow: 'hidden',
    width: 48,
  },
  iconButtonInner: {
    alignItems: 'center',
    borderRadius: 20,
    height: 40,
    justifyContent: 'center',
    width: 40,
  },
  iconGlyph: {
    color: unisonColors.textNdPrimary,
    fontSize: 24,
    lineHeight: 24,
  },
  selectorLabelGroup: {
    alignItems: 'center',
    minWidth: 60,
  },
  selectorLabel: {
    color: unisonColors.textNdPrimary,
    fontSize: 14,
    fontWeight: '500',
    letterSpacing: 0.1,
    lineHeight: 20,
  },
  selectorValue: {
    color: unisonColors.textNdPrimary,
    fontSize: 12,
    lineHeight: 16,
  },
  calendarGrid: {
    paddingBottom: 4,
    paddingHorizontal: 12,
  },
  weekdayRow: {
    flexDirection: 'row',
  },
  weekdayCell: {
    alignItems: 'center',
    flex: 1,
    height: 48,
    justifyContent: 'center',
  },
  weekdayText: {
    color: unisonColors.textNdPrimary,
    fontSize: 16,
    lineHeight: 24,
  },
  weekRow: {
    flexDirection: 'row',
  },
  dayCell: {
    alignItems: 'center',
    flex: 1,
    height: 48,
    justifyContent: 'center',
  },
  dayButton: {
    alignItems: 'center',
    borderRadius: 20,
    height: 40,
    justifyContent: 'center',
    overflow: 'hidden',
    width: 40,
  },
  dayText: {
    color: unisonColors.textNdPrimary,
    fontSize: 16,
    lineHeight: 24,
  },
  overflowDayText: {
    color: unisonColors.textNdMuted,
    opacity: 0.38,
  },
  selectedDayText: {
    color: unisonColors.white,
  },
  actionRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    paddingBottom: 8,
    paddingHorizontal: 8,
  },
  timePickerBackdrop: {
    backgroundColor: 'rgba(0,0,0,0.5)',
    flex: 1,
  },
  timePickerCenter: {
    alignItems: 'center',
    flex: 1,
    justifyContent: 'center',
  },
  timePickerCard: {
    backgroundColor: unisonColors.white,
    borderRadius: 28,
    elevation: 3,
    minWidth: 328,
    paddingVertical: 8,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 5 },
    shadowOpacity: 0.34,
    shadowRadius: 6.27,
  },
  timePickerLabel: {
    paddingHorizontal: 24,
    paddingTop: 16,
  },
  timePickerLabelText: {
    color: unisonColors.textNdPrimary,
    fontSize: 12,
    letterSpacing: 0.5,
    lineHeight: 16,
  },
  timePickerContent: {
    paddingBottom: 16,
    paddingHorizontal: 24,
    paddingTop: 20,
  },
  timePickerActions: {
    alignItems: 'center',
    flexDirection: 'row',
    padding: 8,
  },
  timePickerActionsSpacer: {
    flex: 1,
  },
});

interface CalendarDayCell {
  readonly key: string;
  readonly date: Date;
  readonly label: string;
  readonly isCurrentMonth: boolean;
  readonly isSelected: boolean;
  readonly isToday: boolean;
}

const toSafeDate = (value: string): Date => {
  const parsedDate = new Date(value);

  if (!Number.isNaN(parsedDate.getTime())) {
    return parsedDate;
  }

  return new Date();
};

const createCalendarDate = (year: number, month: number, day: number): Date =>
  new Date(year, month, day, 12, 0, 0, 0);

const startOfMonth = (value: Date): Date =>
  createCalendarDate(value.getFullYear(), value.getMonth(), 1);

const addDays = (value: Date, days: number): Date =>
  createCalendarDate(value.getFullYear(), value.getMonth(), value.getDate() + days);

const shiftCalendarMonth = (value: Date, delta: number): Date =>
  createCalendarDate(value.getFullYear(), value.getMonth() + delta, 1);

const shiftCalendarYear = (value: Date, delta: number): Date =>
  createCalendarDate(value.getFullYear() + delta, value.getMonth(), 1);

const mergeDateWithTime = (dateValue: Date, timeValue: Date): Date => {
  const nextDate = new Date(dateValue);
  nextDate.setHours(timeValue.getHours(), timeValue.getMinutes(), 0, 0);

  return nextDate;
};

const isSameDay = (left: Date, right: Date): boolean =>
  left.getFullYear() === right.getFullYear() &&
  left.getMonth() === right.getMonth() &&
  left.getDate() === right.getDate();

const buildCalendarCells = (displayedMonthDate: Date, selectedDate: Date): CalendarDayCell[] => {
  const monthStart = startOfMonth(displayedMonthDate);
  const calendarStart = addDays(monthStart, -monthStart.getDay());
  const today = new Date();

  return Array.from({ length: 42 }, (_, index) => {
    const cellDate = addDays(calendarStart, index);

    return {
      key: cellDate.toISOString(),
      date: cellDate,
      label: String(cellDate.getDate()),
      isCurrentMonth: cellDate.getMonth() === displayedMonthDate.getMonth(),
      isSelected: isSameDay(cellDate, selectedDate),
      isToday: isSameDay(cellDate, today),
    };
  });
};

const formatMonthValue = (value: Date, locale: string): string =>
  new Intl.DateTimeFormat(locale, { month: 'short' }).format(value);

const formatYearValue = (value: Date, locale: string): string =>
  new Intl.DateTimeFormat(locale, { year: 'numeric' }).format(value);

const formatWeekdayLabels = (locale: string): string[] => {
  const weekdayFormatter = new Intl.DateTimeFormat(locale, { weekday: 'narrow' });
  const sunday = createCalendarDate(2023, 0, 1);

  return Array.from({ length: 7 }, (_, index) => weekdayFormatter.format(addDays(sunday, index)));
};

const formatDayAccessibilityLabel = (value: Date, locale: string): string =>
  new Intl.DateTimeFormat(locale, { dateStyle: 'full' }).format(value);

interface CalendarSelectorProps {
  readonly label: string;
  readonly value: string;
  readonly previousAccessibilityLabel: string;
  readonly nextAccessibilityLabel: string;
  readonly onPrevious: () => void;
  readonly onNext: () => void;
}

const CalendarSelector = ({
  label,
  value,
  previousAccessibilityLabel,
  nextAccessibilityLabel,
  onPrevious,
  onNext,
}: CalendarSelectorProps) => (
  <View style={styles.selectionGroup}>
    <TouchableRipple
      accessibilityLabel={previousAccessibilityLabel}
      accessibilityRole="button"
      onPress={onPrevious}
      rippleColor={rippleOnSurface}
      style={styles.iconButton}>
      <View style={styles.iconButtonInner}>
        <Text style={styles.iconGlyph}>‹</Text>
      </View>
    </TouchableRipple>
    <View style={styles.selectorLabelGroup}>
      <Text style={styles.selectorLabel}>{label}</Text>
      <Text style={styles.selectorValue}>{value}</Text>
    </View>
    <TouchableRipple
      accessibilityLabel={nextAccessibilityLabel}
      accessibilityRole="button"
      onPress={onNext}
      rippleColor={rippleOnSurface}
      style={styles.iconButton}>
      <View style={styles.iconButtonInner}>
        <Text style={styles.iconGlyph}>›</Text>
      </View>
    </TouchableRipple>
  </View>
);

interface CalendarDayButtonProps {
  readonly cell: CalendarDayCell;
  readonly locale: string;
  readonly onPress: (date: Date) => void;
  readonly selectedBackgroundColor: string;
}

const CalendarDayButton = ({
  cell,
  locale,
  onPress,
  selectedBackgroundColor,
}: CalendarDayButtonProps) => {
  const dayButtonStyles = cell.isSelected
    ? [styles.dayButton, { backgroundColor: selectedBackgroundColor }]
    : styles.dayButton;
  const dayTextStyles = [
    styles.dayText,
    !cell.isCurrentMonth ? styles.overflowDayText : null,
    cell.isSelected ? styles.selectedDayText : null,
  ];
  const accessibilityLabel = formatDayAccessibilityLabel(cell.date, locale);

  return (
    <View style={styles.dayCell}>
      <TouchableRipple
        accessibilityLabel={accessibilityLabel}
        accessibilityRole="button"
        onPress={() => onPress(cell.date)}
        rippleColor={rippleOnSurface}
        style={dayButtonStyles}>
        <Text style={dayTextStyles}>{cell.label}</Text>
      </TouchableRipple>
    </View>
  );
};

const combineDateAndTime = (date: Date, hours: number, minutes: number): string => {
  const nextDate = new Date(date);
  nextDate.setHours(hours, minutes, 0, 0);

  return nextDate.toISOString();
};

const MeetingDateTimePickerDialog = ({
  visible,
  initialDateTime,
  locale,
  onDismiss,
  onConfirm,
}: MeetingDateTimePickerDialogProps) => {
  const theme = useAppTheme();
  const { t } = useTranslation();
  const [pickerStep, setPickerStep] = useState<'date' | 'time'>('date');
  const [selectedDate, setSelectedDate] = useState(() => toSafeDate(initialDateTime));
  const [displayedMonthDate, setDisplayedMonthDate] = useState(() =>
    startOfMonth(toSafeDate(initialDateTime)),
  );
  const [timePickerFocused, setTimePickerFocused] = useState<'hours' | 'minutes'>('hours');
  const [timePickerHours, setTimePickerHours] = useState(() =>
    toSafeDate(initialDateTime).getHours(),
  );
  const [timePickerMinutes, setTimePickerMinutes] = useState(() =>
    toSafeDate(initialDateTime).getMinutes(),
  );

  const calendarCells = useMemo(
    () => buildCalendarCells(displayedMonthDate, selectedDate),
    [displayedMonthDate, selectedDate],
  );
  const weekdayLabels = useMemo(() => formatWeekdayLabels(locale), [locale]);

  const monthLabel = t('meetings.detail.month', { defaultValue: 'Month' });
  const yearLabel = t('meetings.detail.year', { defaultValue: 'Year' });

  const handleDismiss = useCallback(() => {
    setPickerStep('date');
    onDismiss();
  }, [onDismiss]);

  const handleSelectDate = useCallback(
    (nextDate: Date) => {
      const mergedDate = mergeDateWithTime(nextDate, selectedDate);

      setSelectedDate(mergedDate);
      setDisplayedMonthDate(startOfMonth(nextDate));
    },
    [selectedDate],
  );

  const handleConfirmDate = useCallback(() => {
    setPickerStep('time');
  }, []);

  const handlePreviousMonth = useCallback(() => {
    setDisplayedMonthDate((currentDate) => shiftCalendarMonth(currentDate, -1));
  }, []);

  const handleNextMonth = useCallback(() => {
    setDisplayedMonthDate((currentDate) => shiftCalendarMonth(currentDate, 1));
  }, []);

  const handlePreviousYear = useCallback(() => {
    setDisplayedMonthDate((currentDate) => shiftCalendarYear(currentDate, -1));
  }, []);

  const handleNextYear = useCallback(() => {
    setDisplayedMonthDate((currentDate) => shiftCalendarYear(currentDate, 1));
  }, []);

  const handleTimePickerFocus = useCallback((type: 'hours' | 'minutes') => {
    setTimePickerFocused(type);
  }, []);

  const handleTimePickerChange = useCallback(
    (params: { hours: number; minutes: number; focused?: 'hours' | 'minutes' }) => {
      if (params.focused) {
        setTimePickerFocused(params.focused);
      }
      setTimePickerHours(params.hours);
      setTimePickerMinutes(params.minutes);
    },
    [],
  );

  const handleConfirmTime = useCallback(
    ({ hours, minutes }: { hours: number; minutes: number }) => {
      setPickerStep('date');
      onConfirm(combineDateAndTime(selectedDate, hours, minutes));
    },
    [onConfirm, selectedDate],
  );

  return (
    <>
      <Modal
        transparent
        animationType="fade"
        presentationStyle="overFullScreen"
        visible={visible && pickerStep === 'date'}
        onRequestClose={handleDismiss}>
        <View testID="meeting-date-time-picker-overlay" style={styles.overlay}>
          <Pressable style={styles.dismissArea} onPress={handleDismiss} />
          <View style={[styles.card, CARD_SHADOW]}>
            <View style={styles.selectionRow}>
              <CalendarSelector
                label={monthLabel}
                value={formatMonthValue(displayedMonthDate, locale)}
                previousAccessibilityLabel={t('meetings.detail.previousMonth', {
                  defaultValue: 'Previous month',
                })}
                nextAccessibilityLabel={t('meetings.detail.nextMonth', {
                  defaultValue: 'Next month',
                })}
                onPrevious={handlePreviousMonth}
                onNext={handleNextMonth}
              />
              <CalendarSelector
                label={yearLabel}
                value={formatYearValue(displayedMonthDate, locale)}
                previousAccessibilityLabel={t('meetings.detail.previousYear', {
                  defaultValue: 'Previous year',
                })}
                nextAccessibilityLabel={t('meetings.detail.nextYear', {
                  defaultValue: 'Next year',
                })}
                onPrevious={handlePreviousYear}
                onNext={handleNextYear}
              />
            </View>

            <View style={styles.calendarGrid}>
              <View style={styles.weekdayRow}>
                {weekdayLabels.map((weekdayLabel, weekdayIndex) => (
                  <View key={`weekday-${weekdayIndex + 1}`} style={styles.weekdayCell}>
                    <Text style={styles.weekdayText}>{weekdayLabel}</Text>
                  </View>
                ))}
              </View>

              {Array.from({ length: 6 }, (_, weekIndex) => {
                const weekCells = calendarCells.slice(weekIndex * 7, weekIndex * 7 + 7);

                return (
                  <View key={`week-${weekIndex + 1}`} style={styles.weekRow}>
                    {weekCells.map((cell) => (
                      <CalendarDayButton
                        key={cell.key}
                        cell={cell}
                        locale={locale}
                        onPress={handleSelectDate}
                        selectedBackgroundColor={unisonColors.iconBrandBase}
                      />
                    ))}
                  </View>
                );
              })}
            </View>

            <View style={styles.actionRow}>
              <Button textColor={unisonColors.onInfoContainer} onPress={handleDismiss}>
                {t('common.cancel', { defaultValue: 'Cancel' })}
              </Button>
              <Button textColor={unisonColors.onInfoContainer} onPress={handleConfirmDate}>
                {t('common.ok', { defaultValue: 'OK' })}
              </Button>
            </View>
          </View>
        </View>
      </Modal>
      <Modal
        transparent
        animationType="none"
        presentationStyle="overFullScreen"
        visible={visible && pickerStep === 'time'}
        onRequestClose={handleDismiss}
        statusBarTranslucent>
        <TouchableWithoutFeedback onPress={handleDismiss}>
          <View style={[StyleSheet.absoluteFill, styles.timePickerBackdrop]} />
        </TouchableWithoutFeedback>
        <PaperProvider
          settings={{ rippleEffectEnabled: false }}
          theme={{
            ...theme,
            colors: {
              ...theme.colors,
              primary: unisonColors.iconBrandBase,
              primaryContainer: unisonColors.brandGreenWeak,
              onPrimaryContainer: unisonColors.textNdPrimary,
              surface: unisonColors.bgNlWeak,
              surfaceVariant: unisonColors.bgNlWeak,
            },
          }}>
          <View style={[StyleSheet.absoluteFill, styles.timePickerCenter]} pointerEvents="box-none">
            <KeyboardAvoidingView style={styles.timePickerCenter} behavior="padding">
              <Animated.View style={styles.timePickerCard}>
                <View style={styles.timePickerLabel}>
                  <Text style={styles.timePickerLabelText}>
                    {t('meetings.detail.selectTime', { defaultValue: 'Select time' })}
                  </Text>
                </View>
                <View style={styles.timePickerContent}>
                  <TimePicker
                    locale={locale}
                    inputType="picker"
                    use24HourClock
                    focused={timePickerFocused}
                    hours={timePickerHours}
                    minutes={timePickerMinutes}
                    onFocusInput={handleTimePickerFocus}
                    onChange={handleTimePickerChange}
                  />
                </View>
                <View style={styles.timePickerActions}>
                  <IconButton
                    icon="keyboard-outline"
                    size={24}
                    onPress={() => Keyboard.dismiss()}
                    accessibilityLabel="toggle keyboard"
                  />
                  <View style={styles.timePickerActionsSpacer} />
                  <Button textColor={unisonColors.onInfoContainer} onPress={handleDismiss}>
                    {t('common.cancel', { defaultValue: 'Cancel' })}
                  </Button>
                  <Button
                    textColor={unisonColors.onInfoContainer}
                    onPress={() =>
                      handleConfirmTime({ hours: timePickerHours, minutes: timePickerMinutes })
                    }>
                    {t('common.ok', { defaultValue: 'OK' })}
                  </Button>
                </View>
              </Animated.View>
            </KeyboardAvoidingView>
          </View>
        </PaperProvider>
      </Modal>
    </>
  );
};

export { MeetingDateTimePickerDialog };
